// src/neetjee_guru/pages/AcademicDashboard.js
import React, { Component } from "react";
import { Container } from "react-bootstrap";
import { withApollo } from "react-apollo";
import { withRouter } from "react-router-dom";
import * as Cookies from "es-cookie";
import { isBrowser } from "react-device-detect";

import SideNavbar from "../components/navbars/SideNavbar";
import NavbarOne from "../components/navbars/NavbarOne";
import Footer from "../components/footer/Footer";

// Planner Components & GraphQL Operations
import FilterControlBar from "../components/academicPlanner/FilterControlBar";
import TimelineViewport from "../components/academicPlanner/TimelineViewport";
import CalendarGridViewport from "../components/academicPlanner/CalendarGridViewport";
import TableViewport from "../components/academicPlanner/TableViewport";
import DetailDrawer from "../components/academicPlanner/DetailDrawer";
import ShiftScheduleModal from "../components/academicPlanner/ShiftScheduleModal";
import PlannerCreationWizard from "../components/academicPlanner/PlannerCreationWizard";
import ConfigurationPanel from "../components/academicPlanner/ConfigurationPanel";
import BulkUploadModal from "../components/academicPlanner/BulkUploadModal";
import {
  GET_ACADEMIC_PLANNER_DETAILS,
  GET_ACADEMIC_PLANNERS,
  UPDATE_PLANNER_DETAIL,
  UPDATE_TEACHING_PROGRESS,
  BULK_SHIFT_PLANNER_SCHEDULE,
  CREATE_ACADEMIC_PLANNER,
  BULK_INSERT_PLANNER_DETAILS,
  GET_BRANCHES_FOR_PLANNER,
  GET_ACADEMIC_YEARS
} from "../graphql/academicPlannerQueries";

class AcademicDashboard extends Component {
  _isMounted = false;

  constructor(props) {
    super(props);
    this.state = {
      allSchedules: {},
      plannersList: [],
      selectedPlannerId: "",
      activeStream: "JEE Mains & EAPCET (Telangana)",
      viewMode: "table", // timeline | table | calendar
      activeSubjects: [], // Selected subject pills
      searchText: "",
      activeCategory: "All", // All, Regular, Exam, Holiday
      selectedDay: null,
      isDrawerOpen: false,
      showGoToTop: false,
      loading: false,
      errorMsg: null,

      // Institute vs Branch & Employee Scope State
      scopeMode: "Institute", // "Institute" (Master) vs "Branch"
      selectedBranch: "",
      selectedSection: "Section A",
      userRole: "Admin", // "Admin" (Academic Coordinator) vs "Teacher" (Faculty)
      isShiftModalOpen: false,
      showCreatePlannerModal: false,

      // Date range filter
      dateFrom: "",
      dateTo: "",

      // Live form data fetched from backend (checks institutionid, institute_id, institution_id)
      institutionId: parseInt(Cookies.get("institutionid") || Cookies.get("institute_id") || Cookies.get("institution_id") || "1", 10),
      branchesList: [],
      sectionsList: [],
      academicYearsList: [],

      // Page-level tab navigation
      activeTab: "details" // "details" | "configuration"
    };
  }

  componentDidMount() {
    this._isMounted = true;
    window.addEventListener("scroll", this.handleScroll);

    // Fetch live form data (branches, years) and planner list in parallel
    this.fetchWizardFormData();
    this.fetchMasterPlannersList();
  }

  /**
   * Fetch live branches and academic years for wizard dropdowns.
   * Runs on mount in parallel — failures are soft-handled so the UI degrades gracefully.
   */
  fetchWizardFormData = async () => {
    const { institutionId } = this.state;
    if (!this.props.client) return;

    try {
      const [branchesRes, yearsRes] = await Promise.all([
        this.props.client.query({
          query: GET_BRANCHES_FOR_PLANNER,
          variables: { institution_id: institutionId },
          fetchPolicy: "cache-first"
        }),
        this.props.client.query({
          query: GET_ACADEMIC_YEARS,
          variables: {},
          fetchPolicy: "cache-first"
        })
      ]);

      const branches = (branchesRes.data && branchesRes.data.getBranches) || [];
      const years = (yearsRes.data && yearsRes.data.getAcademicYears) || [];

      // Derive sections from the first branch (default context)
      const defaultBranch = branches.length > 0 ? branches[0] : null;
      const sections = defaultBranch && defaultBranch.sections ? defaultBranch.sections : [];

      if (this._isMounted) {
        this.setState({
          branchesList: branches,
          sectionsList: sections,
          academicYearsList: years,
          // Pre-select first branch from live data
          selectedBranch: defaultBranch ? String(defaultBranch.id) : ""
        });
      }
    } catch (e) {
      console.warn("Notice: Could not fetch wizard form data from backend:", e.message);
    }
  };

  fetchMasterPlannersList = async () => {
    try {
      if (this.props.client) {
        const res = await this.props.client.query({
          query: GET_ACADEMIC_PLANNERS,
          variables: { institution_id: this.state.institutionId },
          fetchPolicy: "network-only"
        });

        if (res && res.data && res.data.getAcademicPlanners && res.data.getAcademicPlanners.length > 0) {
          if (this._isMounted) {
            const planners = res.data.getAcademicPlanners;
            this.setState(state => ({
              plannersList: planners,
              selectedPlannerId: state.selectedPlannerId ? state.selectedPlannerId : String(planners[0].id)
            }), () => {
              this.fetchPlannerData();
            });
          }
        } else {
          // Senior Dev Flow: If no planner exists yet, start directly in Configuration Mode!
          if (this._isMounted) {
            this.setState({
              plannersList: [],
              activeTab: "configuration"
            });
          }
        }
      }
    } catch (e) {
      console.warn("Notice: Standard query failed, retrying master planners query:", e.message);
      // Resilient fallback query if backend is in middle of schema cache reload
      try {
        if (this.props.client && this._isMounted) {
          const res = await this.props.client.query({
            query: GET_ACADEMIC_PLANNERS,
            variables: {},
            fetchPolicy: "network-only"
          });
          if (res && res.data && res.data.getAcademicPlanners && res.data.getAcademicPlanners.length > 0) {
            const planners = res.data.getAcademicPlanners;
            this.setState(state => ({
              plannersList: planners,
              selectedPlannerId: state.selectedPlannerId ? state.selectedPlannerId : String(planners[0].id)
            }), () => {
              this.fetchPlannerData();
            });
          }
        }
      } catch (err2) {
        console.warn("Notice: Fallback query also logged:", err2.message);
      }
    }
  };

  fetchPlannerData = async (forceRefresh = false) => {
    const defaultSubjects = this.getStreamSubjects(this.state.activeStream);

    if (this._isMounted) {
      this.setState({
        activeSubjects: defaultSubjects,
        loading: true,
        errorMsg: null
      });
    }

    try {
      if (this.props.client) {
        const queryVars = {
          academicPlannerId: String(this.state.selectedPlannerId || "1"),
          sectionName: this.state.selectedSection
        };

        if (this.state.dateFrom) queryVars.startDate = this.state.dateFrom;
        if (this.state.dateTo) queryVars.endDate = this.state.dateTo;

        const res = await this.props.client.query({
          query: GET_ACADEMIC_PLANNER_DETAILS,
          variables: queryVars,
          fetchPolicy: forceRefresh ? "network-only" : "cache-first"
        });

        if (res && res.data && res.data.getAcademicPlannerDetails && res.data.getAcademicPlannerDetails.length > 0) {
          const backendRows = res.data.getAcademicPlannerDetails;

          // ── DEBUG: See what comes back from GraphQL ──
          console.log("[DEBUG] fetchPlannerData: plannerID=", this.state.selectedPlannerId, "rows count=", backendRows.length, "first row sample=", backendRows[0]);

          const groupedMap = {};
          const todayObj = new Date();
          const todayStr = `${todayObj.getFullYear()}-${String(todayObj.getMonth()+1).padStart(2,'0')}-${String(todayObj.getDate()).padStart(2,'0')}`;

          backendRows.forEach(row => {
            const dateStr = row.schedule_date;
            if (!dateStr) return; // skip null dates
            if (!groupedMap[dateStr]) {
              const d = new Date(dateStr + "T00:00:00"); // force local date parse
              groupedMap[dateStr] = {
                id: row.id,
                date: dateStr,
                isToday: dateStr === todayStr,
                dayNumber: row.day_number,
                weekNumber: row.week_number,
                weekName: `Week ${row.week_number}`,
                dayName: d.toLocaleDateString("en-US", { weekday: "long" }),
                dayOfWeek: d.getDay(),
                type: row.day_type || "Regular",
                subType: row.sub_type || "",
                title: row.day_type === "Exam" ? (row.exam_title || `Exam Day ${row.day_number}`) : (row.day_type === "Holiday" ? (row.sub_type || "Holiday") : "Regular Curriculum Syllabus"),
                description: row.day_type === "Holiday" ? "Institutional Holiday" : "Teaching & Classroom Practice",
                topics: {},
                teachingStatus: {},
                detailIds: {}
              };
            }

            if (row.subject_name) {
              groupedMap[dateStr].topics[row.subject_name] = row.topic_title || "";
              groupedMap[dateStr].detailIds[row.subject_name] = row.id;

              let progressObj = {};
              try {
                progressObj = row.section_progress_json
                  ? (typeof row.section_progress_json === "string"
                    ? JSON.parse(row.section_progress_json)
                    : row.section_progress_json)
                  : {};
              } catch (parseErr) {
                console.warn("[DEBUG] section_progress_json parse error for row id=", row.id, parseErr.message);
                progressObj = {};
              }
              groupedMap[dateStr].teachingStatus[row.subject_name] = progressObj[this.state.selectedSection] || row.overall_status || "PENDING";
            }
          });

          // Industry-Level Dynamic Subject Extraction from DB Rows
          const detectedSubjects = Array.from(
            new Set(backendRows.map(r => r.subject_name).filter(Boolean))
          );
          const dynamicSubjects = detectedSubjects.length > 0 ? detectedSubjects : defaultSubjects;

          const formattedList = Object.values(groupedMap).sort((a, b) => new Date(a.date) - new Date(b.date));

          const plannerIdKey = String(this.state.selectedPlannerId || "1");

          // ── DEBUG: See what ends up in formattedList ──
          console.log("[DEBUG] fetchPlannerData: plannerIdKey=", plannerIdKey, "formattedList.length=", formattedList.length, "detectedSubjects=", detectedSubjects);

          if (this._isMounted) {
            this.setState({
              activeSubjects: dynamicSubjects,
              allSchedules: {
                ...this.state.allSchedules,
                [plannerIdKey]: formattedList,
                [this.state.activeStream]: formattedList
              },
              loading: false
            }, () => {
              // ── Auto-scroll to today's row after data renders ──
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  this.jumpToToday();
                });
              });
            });
          }
        } else {
          // If planner has 0 details rows yet, clear state for plannerIdKey & activeStream
          const plannerIdKey = String(this.state.selectedPlannerId || "1");
          if (this._isMounted) {
            this.setState({
              allSchedules: {
                ...this.state.allSchedules,
                [plannerIdKey]: [],
                [this.state.activeStream]: []
              },
              loading: false
            });
          }
        }
      } else {
        if (this._isMounted) this.setState({ loading: false });
      }
    } catch (err) {
      const plannerIdKey = String(this.state.selectedPlannerId || "1");
      if (this._isMounted) {
        this.setState({
          allSchedules: {
            ...this.state.allSchedules,
            [plannerIdKey]: [],
            [this.state.activeStream]: []
          },
          loading: false
        });
      }
    }

    setTimeout(() => {
      if (this._isMounted) this.jumpToToday();
    }, 400);
  };

  componentWillUnmount() {
    this._isMounted = false;
    window.removeEventListener("scroll", this.handleScroll);
  }

  handleScroll = () => {
    // Show Go to Top button if scrolled down past 250px
    const showGoToTop = window.scrollY > 250;
    if (showGoToTop !== this.state.showGoToTop) {
      this.setState({ showGoToTop });
    }
  };

  // Helper to determine stream subjects
  getStreamSubjects = (streamName) => {
    if (streamName === "NEET" || streamName === "Senior Inter (BiPC)") {
      return ["Botany", "Zoology", "Physics", "Chemistry"];
    }
    return ["Mathematics", "Physics", "Chemistry"];
  };

  // Menu toggler for sidebar support
  menuToggler = () => {
    const toggled = Cookies.get("toggle");
    if (toggled === "wrapper") {
      Cookies.set("toggle", "wrapper sidebar-enable");
    } else {
      Cookies.set("toggle", "wrapper");
    }
    this.forceUpdate();
  };

  handleScopeModeChange = (scopeMode) => {
    this.setState({ scopeMode });
  };

  handleBranchChange = (selectedBranch) => {
    this.setState({ selectedBranch });
  };

  handleSectionChange = (selectedSection) => {
    this.setState({ selectedSection }, () => {
      this.fetchPlannerData();
    });
  };

  handleRoleChange = (userRole) => {
    this.setState({ userRole });
  };

  handleSaveDay = (updatedDay) => {
    const { allSchedules, activeStream } = this.state;
    const streamSchedule = allSchedules[activeStream] || [];
    const updatedList = streamSchedule.map(item => {
      if (item.date === updatedDay.date) {
        return updatedDay;
      }
      return item;
    });

    this.setState({
      allSchedules: {
        ...allSchedules,
        [activeStream]: updatedList
      },
      selectedDay: updatedDay
    });

    if (this.props.client && updatedDay && updatedDay.id) {
      this.props.client.mutate({
        mutation: UPDATE_PLANNER_DETAIL,
        variables: {
          input: {
            id: parseInt(updatedDay.id, 10),
            topic_title: updatedDay.topic || "",
            day_type: updatedDay.dayType || "Regular",
            sub_type: updatedDay.subType || "",
            exam_title: updatedDay.examTitle || ""
          }
        }
      }).catch(e => console.warn("Update planner detail error:", e));
    }
  };

  handleUpdateStatus = (dateStr, subjectName, newStatus) => {
    const { allSchedules, activeStream } = this.state;
    const streamSchedule = allSchedules[activeStream] || [];
    let detailIdToUpdate = null;
    
    const updatedList = streamSchedule.map(item => {
      if (item.date === dateStr) {
        if (item.detailIds && item.detailIds[subjectName]) {
          detailIdToUpdate = item.detailIds[subjectName];
        } else if (item.id) {
          detailIdToUpdate = item.id;
        }
        const currentStatusMap = item.teachingStatus || {};
        return {
          ...item,
          teachingStatus: {
            ...currentStatusMap,
            [subjectName]: newStatus
          }
        };
      }
      return item;
    });

    this.setState({
      allSchedules: {
        ...allSchedules,
        [activeStream]: updatedList
      }
    });

    if (this.props.client && detailIdToUpdate) {
      this.props.client.mutate({
        mutation: UPDATE_TEACHING_PROGRESS,
        variables: {
          input: {
            detail_id: parseInt(detailIdToUpdate, 10),
            section_name: this.state.selectedSection,
            teaching_status: newStatus,
            employee_id: 101
          }
        }
      }).catch(e => console.warn("Update teaching progress error:", e));
    }
  };

  handleOpenShiftModal = () => {
    this.setState({ isShiftModalOpen: true });
  };

  handleCloseShiftModal = () => {
    this.setState({ isShiftModalOpen: false });
  };

  handleBulkShiftSchedule = (fromDateStr, shiftDaysCount, reason) => {
    const { allSchedules, activeStream } = this.state;
    const streamSchedule = allSchedules[activeStream] || [];

    const updatedList = streamSchedule.map(item => {
      if (item.date >= fromDateStr) {
        const itemDate = new Date(item.date);
        itemDate.setDate(itemDate.getDate() + shiftDaysCount);
        const year = itemDate.getFullYear();
        const month = String(itemDate.getMonth() + 1).padStart(2, '0');
        const day = String(itemDate.getDate()).padStart(2, '0');
        const newDateStr = `${year}-${month}-${day}`;

        return {
          ...item,
          date: newDateStr,
          description: `${item.description} (Shifted by ${shiftDaysCount} day: ${reason})`
        };
      }
      return item;
    });

    this.setState({
      allSchedules: {
        ...allSchedules,
        [activeStream]: updatedList
      }
    });

    if (this.props.client) {
      this.props.client.mutate({
        mutation: BULK_SHIFT_PLANNER_SCHEDULE,
        variables: {
          input: {
            academic_planner_id: parseInt(this.state.selectedPlannerId || "1", 10),
            from_date: fromDateStr,
            shift_days: parseInt(shiftDaysCount, 10),
            reason: reason
          }
        }
      }).catch(e => console.warn("Bulk shift schedule error:", e));
    }
  };

  handleCreatePlanner = async (plannerPayload) => {
    let newPlannerId = null;
    try {
      if (this.props.client) {
        const res = await this.props.client.mutate({
          mutation: CREATE_ACADEMIC_PLANNER,
          variables: {
            input: {
              institution_id: this.state.institutionId,
              academic_planner: plannerPayload.academic_planner,
              academic_year_id: plannerPayload.academic_year_id,
              stream_name: plannerPayload.stream_name,
              state_ids: plannerPayload.state_ids,
              branch_ids: plannerPayload.branch_ids,
              class_ids: plannerPayload.class_ids,
              section_ids: plannerPayload.section_ids,
              start_date: plannerPayload.start_date
            }
          }
        });

        if (res && res.data && res.data.createAcademicPlanner && res.data.createAcademicPlanner.planner_id) {
          newPlannerId = res.data.createAcademicPlanner.planner_id;
        }
      }
    } catch (err) {
      console.warn("Could not push new planner to server:", err.message);
    }

    // Optimistically create new planner object for instant local UI display
    const targetPlannerId = newPlannerId ? String(newPlannerId) : `temp-${Date.now()}`;
    const optimisticPlanner = {
      id: targetPlannerId,
      academic_planner: plannerPayload ? plannerPayload.academic_planner : "New Academic Planner",
      stream_name: plannerPayload ? plannerPayload.stream_name : this.state.activeStream,
      academic_year_id: plannerPayload ? plannerPayload.academic_year_id : 1,
      estatus: 1,
      map_details: {
        branch_ids: plannerPayload ? (plannerPayload.branch_ids || "ALL") : "ALL",
        section_ids: plannerPayload ? (plannerPayload.section_ids || "ALL") : "ALL",
        class_ids: plannerPayload ? (plannerPayload.class_ids || "ALL") : "ALL",
        state_ids: plannerPayload ? (plannerPayload.state_ids || "ALL") : "ALL"
      }
    };

    // Refresh planner list + auto-select newly created planner ID instantly
    this.setState(state => {
      const existingList = state.plannersList || [];
      const updatedList = existingList.some(p => String(p.id) === targetPlannerId)
        ? existingList
        : [optimisticPlanner, ...existingList];

      return {
        plannersList: updatedList,
        activeStream: (plannerPayload && plannerPayload.stream_name) ? plannerPayload.stream_name : state.activeStream,
        selectedPlannerId: targetPlannerId,
        activeTab: "configuration"
      };
    }, () => {
      // Force network refetch so server data overrides local state smoothly
      this.fetchMasterPlannersList();
    });
  };

  handleBulkUpload = async ({ plannerId, rows }) => {
    try {
      if (this.props.client) {
        await this.props.client.mutate({
          mutation: BULK_INSERT_PLANNER_DETAILS,
          variables: {
            academic_planner_id: String(plannerId || this.state.selectedPlannerId || "1"),
            rows: rows.map(r => ({
              day_number: parseInt(r.day_number, 10),
              schedule_date: r.schedule_date,
              subject_name: r.subject_name,
              topic_title: r.topic_title,
              day_type: r.day_type || "Regular",
              sub_type: r.sub_type || "",
              exam_title: r.exam_title || "",
              overall_status: r.overall_status || "PENDING"
            }))
          }
        });
      }
    } catch (err) {
      console.warn("Notice: Bulk upload mutation logged:", err.message);
    }

    // Senior Dev Flow: Auto-switch to Planner Details tab & re-fetch to visualize inserted bulk data instantly!
    this.setState({
      selectedPlannerId: String(plannerId || this.state.selectedPlannerId),
      activeTab: "details"
    }, () => {
      this.fetchPlannerData(true);
    });
  };

  handlePlannerChange = (plannerId) => {
    const { plannersList } = this.state;
    const foundPlanner = plannersList.find(p => String(p.id) === String(plannerId));
    const streamName = foundPlanner ? (foundPlanner.stream_name || this.state.activeStream) : this.state.activeStream;
    const defaultSubjects = this.getStreamSubjects(streamName);

    this.setState({
      selectedPlannerId: String(plannerId),
      activeStream: streamName,
      activeSubjects: defaultSubjects,
      searchText: "",
      loading: true
    }, () => {
      // Force network refresh so Apollo bypasses stale cache when switching planners
      this.fetchPlannerData(true);
    });
  };

  handleStreamChange = (newStreamName) => {
    const defaultSubjects = this.getStreamSubjects(newStreamName);
    this.setState({
      activeStream: newStreamName,
      activeSubjects: defaultSubjects,
      searchText: ""
    }, () => {
      // Auto-redirect viewport to today's date context on active planner stream selection change
      setTimeout(() => {
        if (this._isMounted) this.jumpToToday();
      }, 150);
    });
  };

  handleViewModeChange = (newMode) => {
    this.setState({ viewMode: newMode }, () => {
      // Re-trigger today focus alignment when view mode toggles
      setTimeout(() => {
        if (this._isMounted) this.jumpToToday();
      }, 100);
    });
  };

  handleSearchChange = (e) => {
    this.setState({ searchText: e.target.value });
  };

  handleDateFromChange = (val) => {
    this.setState({ dateFrom: val }, () => {
      this.fetchPlannerData(true);
    });
  };

  handleDateToChange = (val) => {
    this.setState({ dateTo: val }, () => {
      this.fetchPlannerData(true);
    });
  };

  handleDateReset = () => {
    this.setState({ dateFrom: "", dateTo: "" }, () => {
      this.fetchPlannerData(true);
    });
  };

  handleSubjectToggle = (subject) => {
    const { activeSubjects } = this.state;
    let nextSubjects = [];
    if (activeSubjects.includes(subject)) {
      nextSubjects = activeSubjects.filter(s => s !== subject);
    } else {
      nextSubjects = [...activeSubjects, subject];
    }
    this.setState({ activeSubjects: nextSubjects });
  };

  handleCategoryChange = (category) => {
    this.setState({ activeCategory: category });
  };

  handleDayClick = (day) => {
    this.setState({
      selectedDay: day,
      isDrawerOpen: true
    });
  };

  handleDrawerClose = () => {
    this.setState({
      isDrawerOpen: false
    });
  };

  getTodayDate = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;

    const { allSchedules, activeStream } = this.state;
    const scheduleList = (allSchedules ? allSchedules[activeStream] : null) || [];
    if (!scheduleList || scheduleList.length === 0) return todayStr;

    const exactMatch = scheduleList.find(item => item.date === todayStr);
    if (exactMatch) return todayStr;

    const firstDate = scheduleList[0].date;
    const lastDate = scheduleList[scheduleList.length - 1].date;

    if (todayStr < firstDate) return firstDate;
    if (todayStr > lastDate) return lastDate;

    let closestDate = firstDate;
    let minDiff = Infinity;
    const todayMs = d.getTime();

    scheduleList.forEach(item => {
      const itemMs = new Date(item.date).getTime();
      const diff = Math.abs(todayMs - itemMs);
      if (diff < minDiff) {
        minDiff = diff;
        closestDate = item.date;
      }
    });

    return closestDate;
  };

  jumpToToday = () => {
    const todayDate = this.getTodayDate();
    // table and timeline both use day-row-{date}; calendar uses calendar-cell-{date}
    const elementId = this.state.viewMode === "calendar"
      ? `calendar-cell-${todayDate}`
      : `day-row-${todayDate}`;
    const element = document.getElementById(elementId);

    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });

      // Apply visual flashing effect to highlight cell/row
      element.classList.add("flash-highlight");
      setTimeout(() => {
        if (element) {
          element.classList.remove("flash-highlight");
        }
      }, 2500);
    }
  };

  scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  render() {
    if (Cookies.get("token") === undefined && this.props.history) {
      this.props.history.push("/login");
    }

    const {
      allSchedules,
      activeStream,
      viewMode,
      activeSubjects,
      searchText,
      activeCategory,
      selectedDay,
      isDrawerOpen,
      showGoToTop
    } = this.state;

    // Resolve schedule arrays for selected planner ID (fallback to active stream)
    const selectedPlannerKey = String(this.state.selectedPlannerId || "");
    const rawStreamSchedules = (allSchedules && (allSchedules[selectedPlannerKey] || allSchedules[activeStream])) || [];
    const { dateFrom, dateTo } = this.state;

    // Resolve active planner name for PDF header
    const activePlannerObj = this.state.plannersList.find(p => String(p.id) === String(this.state.selectedPlannerId));
    const activePlannerName = activePlannerObj ? activePlannerObj.academic_planner : "Academic Master Planner";

    // Filter days based on search query, category, date range, and active subjects
    const filteredDays = rawStreamSchedules.filter(day => {
      // 1. Date range filter — exact YYYY-MM-DD string comparison
      if (dateFrom && day.date < dateFrom) return false;
      if (dateTo && day.date > dateTo) return false;

      // 2. Filter by category
      if (activeCategory !== "All") {
        if (day.type !== activeCategory) return false;
      }

      // 3. Subject filter is handled per column/row inside Table, Timeline, and Calendar viewports

      // 4. Filter by search query text
      if (searchText.trim() !== "") {
        const query = searchText.toLowerCase();
        const matchesTitle = (day.title || "").toLowerCase().includes(query);
        const matchesDesc = (day.description || "").toLowerCase().includes(query);
        const matchesDate = (day.date || "").includes(query);
        const matchesDay = (day.dayName || "").toLowerCase().includes(query);

        let matchesSubject = false;
        Object.keys(day.topics || {}).forEach(sub => {
          if ((day.topics[sub] || "").toLowerCase().includes(query)) {
            matchesSubject = true;
          }
        });

        if (!matchesTitle && !matchesDesc && !matchesDate && !matchesDay && !matchesSubject) {
          return false;
        }
      }

      return true;
    });

    return (
      <div className={Cookies.get("toggle") || "wrapper"}>
        {/* Scoped Dashboard Layout Stylesheet */}
        <style dangerouslySetInnerHTML={{__html: `
          .ds-planner-dashboard {
            background-color: var(--ds-canvas);
            min-height: 100vh;
            padding: 24px 28px;
            font-family: inherit;
          }
          .ds-sticky-header {
            border-bottom: 1.5px solid var(--ds-line) !important;
            padding-bottom: 16px !important;
            margin-bottom: 16px !important;
          }
          .ds-viewport-scroll-area {
            max-height: calc(100vh - 280px) !important;
            overflow-y: auto !important;
            padding-top: 10px !important;
            padding-right: 6px !important;
          }
          /* Custom sleek scrollbar for viewport area */
          .ds-viewport-scroll-area::-webkit-scrollbar {
            width: 6px !important;
          }
          .ds-viewport-scroll-area::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.02) !important;
            border-radius: 4px !important;
          }
          .ds-viewport-scroll-area::-webkit-scrollbar-thumb {
            background: #cbd5e1 !important;
            border-radius: 4px !important;
          }



          /* Floating Back to Top button styling placed inside stacking context */
          .floating-back-to-top {
            position: fixed !important;
            bottom: 30px !important;
            right: 30px !important;
            width: 48px !important;
            height: 48px !important;
            border-radius: 50% !important;
            background-color: #7c3aed !important;
            color: #ffffff !important;
            border: none !important;
            box-shadow: 0 4px 12px rgba(124, 58, 237, 0.4) !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            font-size: 18px !important;
            cursor: pointer !important;
            z-index: 99999 !important;
            pointer-events: auto !important;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          .floating-back-to-top:hover {
            background-color: #6d28d9 !important;
            transform: translateY(-3px) !important;
            box-shadow: 0 6px 16px rgba(124, 58, 237, 0.5) !important;
          }
          .floating-back-to-top i {
            color: #ffffff !important;
            font-size: 18px !important;
          }

          /* Pill Selector Navigation Layout */
          .stream-pills-scroll-track {
            overflow-x: auto;
            white-space: nowrap;
            padding-bottom: 8px;
            -webkit-overflow-scrolling: touch;
          }
          .stream-pills-scroll-track::-webkit-scrollbar {
            height: 5px;
          }
          .stream-pills-scroll-track::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.05);
            border-radius: 9px;
          }
          .stream-pills-scroll-track::-webkit-scrollbar-thumb {
            background: var(--ds-line);
            border-radius: 9px;
          }
          .stream-pills-container {
            display: flex;
            gap: 10px;
          }
          .stream-pill-nav-btn {
            background: var(--ds-surface);
            border: 1px solid var(--ds-line);
            padding: 9px 18px;
            border-radius: var(--ds-r-pill);
            font-weight: 700;
            font-size: 13px;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            transition: all 0.15s ease-in-out;
            box-shadow: var(--ds-sh-1);
          }
          .stream-pill-nav-btn:hover {
            border-color: var(--ds-brand-400);
            background: var(--ds-brand-050);
          }
          .stream-pill-nav-btn.is-active,
          .stream-pill-nav-btn.is-active:focus,
          .stream-pill-nav-btn.is-active:active,
          .stream-pill-nav-btn.is-active:hover {
            background-color: #7c3aed !important;
            background: #7c3aed !important;
            color: #ffffff !important;
            border-color: #7c3aed !important;
          }
          .stream-pill-nav-btn.is-active *,
          .stream-pill-nav-btn.is-active:focus *,
          .stream-pill-nav-btn.is-active:active * {
            background-color: transparent !important;
            background: transparent !important;
            color: #ffffff !important;
          }
          .stream-pill-nav-btn:not(.is-active),
          .stream-pill-nav-btn:not(.is-active):focus,
          .stream-pill-nav-btn:not(.is-active):active {
            color: var(--ds-ink-700) !important;
            background-color: var(--ds-surface) !important;
          }
          .stream-pill-nav-btn:not(.is-active) * {
            color: var(--ds-ink-700) !important;
          }
          .stream-pill-nav-btn .pill-icon {
            font-size: 14px;
          }

          /* Premium Search field Focus styling */
          .premium-search-input {
            border: 1px solid #cbd5e1 !important;
            border-radius: var(--ds-r-sm) !important;
            height: 38px !important;
            color: var(--ds-ink-800) !important;
            transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
          }
          .premium-search-input:focus {
            border-color: var(--ds-brand-400) !important;
            box-shadow: var(--ds-focus) !important;
          }

          /* Premium Buttons and category pills styling overrides */
          .premium-btn {
            background: var(--ds-surface);
            border: 1.5px solid var(--ds-line);
            padding: 7px 14px;
            border-radius: var(--ds-r-sm);
            font-weight: 700;
            font-size: 12.5px;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            transition: all 0.12s ease-in-out;
            box-shadow: var(--ds-sh-1);
          }
          .premium-btn:hover {
            background: var(--ds-brand-050);
            border-color: var(--ds-brand-400);
          }
          .premium-btn.is-active,
          .premium-btn.is-active:focus,
          .premium-btn.is-active:active,
          .premium-btn.is-active:hover {
            background-color: #7c3aed !important;
            background: #7c3aed !important;
            color: #ffffff !important;
            border-color: #7c3aed !important;
          }
          .premium-btn.is-active *,
          .premium-btn.is-active:focus *,
          .premium-btn.is-active:active * {
            background-color: transparent !important;
            background: transparent !important;
            color: #ffffff !important;
          }
          .premium-btn:not(.is-active),
          .premium-btn:not(.is-active):focus,
          .premium-btn:not(.is-active):active {
            color: var(--ds-ink-700) !important;
            background-color: var(--ds-surface) !important;
          }

          .premium-category-btn {
            background: var(--ds-surface);
            border: 1.5px solid var(--ds-line);
            padding: 7px 14px;
            border-radius: var(--ds-r-sm);
            font-weight: 700;
            font-size: 12.5px;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            transition: all 0.12s ease-in-out;
            box-shadow: var(--ds-sh-1);
          }
          .premium-category-btn:hover {
            background: var(--ds-brand-050);
            border-color: var(--ds-brand-400);
          }
          .premium-category-btn.is-active,
          .premium-category-btn.is-active:focus,
          .premium-category-btn.is-active:active,
          .premium-category-btn.is-active:hover {
            background-color: #7c3aed !important;
            background: #7c3aed !important;
            color: #ffffff !important;
            border-color: #7c3aed !important;
          }
          .premium-category-btn.is-active *,
          .premium-category-btn.is-active:focus *,
          .premium-category-btn.is-active:active * {
            background-color: transparent !important;
            background: transparent !important;
            color: #ffffff !important;
          }
          .premium-category-btn.is-exam-active,
          .premium-category-btn.is-exam-active:focus,
          .premium-category-btn.is-exam-active:active,
          .premium-category-btn.is-exam-active:hover {
            background-color: var(--ds-info) !important;
            background: var(--ds-info) !important;
            color: #ffffff !important;
            border-color: var(--ds-info) !important;
          }
          .premium-category-btn.is-exam-active *,
          .premium-category-btn.is-exam-active:focus *,
          .premium-category-btn.is-exam-active:active * {
            background-color: transparent !important;
            background: transparent !important;
            color: #ffffff !important;
          }
          .premium-category-btn.is-holiday-active,
          .premium-category-btn.is-holiday-active:focus,
          .premium-category-btn.is-holiday-active:active,
          .premium-category-btn.is-holiday-active:hover {
            background-color: var(--ds-crit) !important;
            background: var(--ds-crit) !important;
            color: #ffffff !important;
            border-color: var(--ds-crit) !important;
          }
          .premium-category-btn.is-holiday-active *,
          .premium-category-btn.is-holiday-active:focus *,
          .premium-category-btn.is-holiday-active:active * {
            background-color: transparent !important;
            background: transparent !important;
            color: #ffffff !important;
          }
          .premium-category-btn.is-inactive,
          .premium-category-btn.is-inactive:focus,
          .premium-category-btn.is-inactive:active {
            color: var(--ds-ink-700) !important;
            background-color: var(--ds-surface) !important;
          }

          /* Subject Focus tags */
          .ds-subject-pill {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            font-weight: 700;
            padding: 6px 14px;
            border-radius: var(--ds-r-pill);
            border: 1.5px solid var(--ds-line);
            background: var(--ds-surface);
            color: var(--ds-ink-700);
            cursor: pointer;
            transition: all 0.15s ease;
          }
          .ds-subject-pill .dot {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: var(--ds-ink-400);
          }
          .ds-subject-pill.is-math .dot { background-color: #6366f1; }
          .ds-subject-pill.is-physics .dot { background-color: #f59e0b; }
          .ds-subject-pill.is-chemistry .dot { background-color: #10b981; }
          .ds-subject-pill.is-botany .dot { background-color: #ec4899; }
          .ds-subject-pill.is-zoology .dot { background-color: #06b6d4; }
          
          .ds-subject-pill.is-math, .ds-subject-pill.is-math:focus, .ds-subject-pill.is-math:active { border-color: #818cf8 !important; background: #e0e7ff !important; color: #3730a3 !important; }
          .ds-subject-pill.is-physics, .ds-subject-pill.is-physics:focus, .ds-subject-pill.is-physics:active { border-color: #fbbf24 !important; background: #fef3c7 !important; color: #78350f !important; }
          .ds-subject-pill.is-chemistry, .ds-subject-pill.is-chemistry:focus, .ds-subject-pill.is-chemistry:active { border-color: #34d399 !important; background: #d1fae5 !important; color: #065f46 !important; }
          .ds-subject-pill.is-botany, .ds-subject-pill.is-botany:focus, .ds-subject-pill.is-botany:active { border-color: #f472b6 !important; background: #fce7f3 !important; color: #9d174d !important; }
          .ds-subject-pill.is-zoology, .ds-subject-pill.is-zoology:focus, .ds-subject-pill.is-zoology:active { border-color: #22d3ee !important; background: #ecfeff !important; color: #0891b2 !important; }
          .ds-subject-pill.is-inactive,
          .ds-subject-pill.is-inactive * {
            opacity: 0.65 !important;
            color: var(--ds-ink-500) !important;
            border-color: var(--ds-line) !important;
            background-color: var(--ds-surface) !important;
          }
          .ds-subject-pill.is-inactive:hover {
            opacity: 0.95 !important;
          }

          /* Flat Card Timeline List */
          .timeline-viewport-wrapper {
            position: relative;
          }
          .timeline-day-card {
            border-radius: var(--ds-r-md);
            margin-bottom: 16px;
            border: 1.5px solid var(--ds-line);
            box-shadow: var(--ds-sh-1);
            position: relative;
            cursor: pointer;
            transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
            z-index: 2;
          }
          .timeline-day-card:hover {
            transform: translateY(-2px);
            box-shadow: var(--ds-sh-2);
            border-color: var(--ds-brand-400);
          }
          .timeline-day-card.is-selected {
            border-color: var(--ds-brand-500);
            box-shadow: 0 0 0 3px rgba(124,58,237,.18);
          }
          .timeline-day-card.card-exam-day {
            border-left: 5px solid #0ea5e9 !important;
            background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%) !important;
          }
          .timeline-day-card.card-holiday-day {
            border-left: 5px solid #f43f5e !important;
            background: linear-gradient(135deg, #ffffff 0%, #fff1f2 100%) !important;
          }
          .timeline-day-card.card-lesson-day {
            border-left: 5px solid #7c3aed !important;
            background: linear-gradient(135deg, #ffffff 0%, #faf5ff 100%) !important;
          }
          .today-target-row {
            border: 2.5px solid var(--ds-brand-500) !important;
            background: linear-gradient(135deg, var(--ds-surface) 0%, #f5f3ff 100%) !important;
            box-shadow: var(--ds-sh-2) !important;
          }
          .today-badge-indicator {
            position: absolute;
            right: 14px;
            top: -10px;
            background: var(--ds-brand-500);
            color: #fff;
            font-size: 9px;
            font-weight: 800;
            padding: 3px 8px;
            border-radius: var(--ds-r-pill);
            letter-spacing: .08em;
          }

          .special-day-description {
            background-color: var(--ds-canvas);
            border: 1px dashed var(--ds-line);
          }
          .subject-columns-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 12px;
            margin-top: 10px;
          }
          .subject-topic-card {
            background-color: #f8fafc !important;
            border: 1.5px solid #e2e8f0 !important;
            border-left-width: 4.5px !important;
            border-left-style: solid !important;
            border-left-color: var(--ds-line) !important;
            border-radius: 8px !important;
            transition: all 0.15s ease-in-out !important;
          }
          .subject-topic-card:hover {
            background-color: #ffffff !important;
            border-color: var(--ds-brand-300) !important;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
          }
          .subject-topic-card.sub-math-border { border-left-color: #6366f1 !important; }
          .subject-topic-card.sub-physics-border { border-left-color: #f59e0b !important; }
          .subject-topic-card.sub-chemistry-border { border-left-color: #10b981 !important; }
          .subject-topic-card.sub-botany-border { border-left-color: #ec4899 !important; }
          .subject-topic-card.sub-zoology-border { border-left-color: #06b6d4 !important; }
          
          .subject-name-tag {
            font-size: 10px;
            letter-spacing: 0.05em;
          }
          .subject-topic-text {
            font-size: 13px !important;
            line-height: 1.45 !important;
            color: var(--ds-ink-900) !important;
            font-weight: 600 !important;
          }

          /* Calendar Viewport Grid rules */
          .calendar-grid-header {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            border-bottom: 2px solid var(--ds-line);
          }
          .calendar-grid-body {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 6px;
          }
          .calendar-grid-cell {
            min-height: 140px !important;
            border: 1px solid #e2e8f0 !important;
            border-radius: var(--ds-r-sm);
            cursor: pointer;
            transition: all 0.15s ease-in-out;
          }
          .calendar-grid-cell:hover {
            border-color: var(--ds-brand-400) !important;
            box-shadow: 0 4px 10px rgba(15, 23, 42, 0.04) !important;
            transform: translateY(-1px);
          }
          .calendar-grid-cell.is-selected-cell {
            border-color: var(--ds-brand-500) !important;
            box-shadow: 0 0 0 3px rgba(124,58,237,.15) !important;
          }
          .calendar-grid-cell.is-exam-cell {
            background-color: #f0f9ff !important;
          }
          .calendar-grid-cell.is-holiday-cell {
            background-color: #fff1f2 !important;
          }
          .calendar-grid-cell.is-regular-cell {
            background-color: #ffffff !important;
          }
          .calendar-grid-cell.is-dimmed {
            opacity: 0.25 !important;
            filter: grayscale(85%) !important;
          }
          .calendar-grid-cell.today-target-cell {
            border: 2px solid var(--ds-brand-500) !important;
            box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1) !important;
          }
          .today-num-badge {
            background: var(--ds-brand-500) !important;
            color: #ffffff !important;
            padding: 2px 6px !important;
            border-radius: 4px !important;
            font-size: 11px !important;
          }
          .cell-topic-row {
            font-size: 10px !important;
            font-weight: 700 !important;
            padding: 2px 6px !important;
            border-radius: 4px !important;
            margin-bottom: 3px !important;
            display: block !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            transition: transform 0.1s ease;
          }
          .cell-topic-row:hover {
            transform: scale(1.02);
          }
          .cell-topic-row.sub-math-pill { background-color: #eff6ff !important; color: #1e40af !important; border-left: 2px solid #3b82f6 !important; }
          .cell-topic-row.sub-physics-pill { background-color: #fffbeb !important; color: #92400e !important; border-left: 2px solid #f59e0b !important; }
          .cell-topic-row.sub-chemistry-pill { background-color: #ecfdf5 !important; color: #065f46 !important; border-left: 2px solid #10b981 !important; }
          .cell-topic-row.sub-botany-pill { background-color: #fdf2f8 !important; color: #9d174d !important; border-left: 2px solid #ec4899 !important; }
          .cell-topic-row.sub-zoology-pill { background-color: #ecfeff !important; color: #0891b2 !important; border-left: 2px solid #06b6d4 !important; }

          .special-event-label {
            font-size: 11px;
            line-height: 1.3;
          }
          .legend-dot {
            display: inline-block;
            width: 8px;
            height: 8px;
            border-radius: 50%;
          }

          /* Flash highlight animation for Jump Anchoring */
          @keyframes flashHighlight {
            0% {
              outline: 5px solid var(--ds-brand-500);
              background-color: #f3ecfe !important;
            }
            100% {
              outline: 0px solid transparent;
            }
          }
          .flash-highlight {
            animation: flashHighlight 1.8s ease-out;
            border-color: var(--ds-brand-500) !important;
          }

          /* Detail drawer modal overlays */
          .drawer-backdrop {
            position: fixed;
            left: 0;
            top: 0;
            right: 0;
            bottom: 0;
            background: rgba(15, 23, 42, 0.35) !important;
            backdrop-filter: blur(8px) !important;
            z-index: 10000;
          }
          .drawer-panel {
            position: fixed !important;
            top: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 500px !important;
            max-width: 100vw;
            background: #ffffff !important;
            box-shadow: -8px 0 32px rgba(15, 23, 42, 0.08) !important;
            border-left: 1.5px solid var(--ds-line);
            z-index: 10001;
            animation: drawerSlideIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
          @keyframes drawerSlideIn {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
          
          .border-left-highlight {
            border: 1px solid #e2e8f0 !important;
            border-left-width: 4.5px !important;
            border-left-style: solid !important;
            border-left-color: var(--ds-line) !important;
            border-radius: 8px !important;
            background: #ffffff !important;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
            transition: all 0.15s ease !important;
          }
          .border-left-highlight:hover {
            border-color: var(--ds-brand-300) !important;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
          }
          .border-left-highlight.is-math-label { border-left-color: #6366f1 !important; }
          .border-left-highlight.is-physics-label { border-left-color: #f59e0b !important; }
          .border-left-highlight.is-chemistry-label { border-left-color: #10b981 !important; }
          .border-left-highlight.is-botany-label { border-left-color: #ec4899 !important; }
          .border-left-highlight.is-zoology-label { border-left-color: #06b6d4 !important; }

          .subject-drawer-label {
            font-size: 10px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            display: inline-block;
            padding: 2px 7px;
            border-radius: 4px;
            margin-bottom: 4px;
          }
          .subject-drawer-label.is-math-label { background: #e0e7ff; color: #3730a3; }
          .subject-drawer-label.is-physics-label { background: #fef3c7; color: #78350f; }
          .subject-drawer-label.is-chemistry-label { background: #d1fae5; color: #065f46; }
          .subject-drawer-label.is-botany-label { background: #fce7f3; color: #9d174d; }
          .subject-drawer-label.is-zoology-label { background: #ecfeff; color: #0891b2; }

          .checklist-item input[type="checkbox"] {
            width: 16px;
            height: 16px;
            accent-color: var(--ds-brand-500);
            cursor: pointer;
          }
          .text-strikethrough {
            text-decoration: line-through;
            opacity: 0.75;
          }
          .notes-textarea:focus {
            border-color: var(--ds-brand-400);
            box-shadow: var(--ds-focus);
          }
        `}} />

        <div className="header-area">
          <NavbarOne onClick={() => this.menuToggler()} />
        </div>
        
        <div className="main-wrapper">
          <div
            id="sidebar"
            className={
              isBrowser === true
                ? "sidebar-menu d-none d-xl-block d-lg-block"
                : "sidebar-menu d-block d-xl-none d-lg-none"
            }
          >
            <SideNavbar onClick={() => this.menuToggler()} />
          </div>
          <div className="student-overlay" onClick={() => this.menuToggler()} />
          
          <div className="content-wrapper pt-4 pb-5">
            <div className="ds-planner-dashboard">
              <Container fluid>

                {/* Dashboard Core Layout Container */}
                <div style={{ background: "#fff", borderRadius: 16, boxShadow: "var(--ds-sh-1)", overflow: "hidden", border: "1px solid var(--ds-line)" }}>
                  <div className="p-4">
                    
                    {/* ── Page Title + Tab Navigation ──────────────────────────────────── */}
                    <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-4">
                      <div>
                        <h2 className="font-weight-black text-dark m-0">
                          <i className="fal fa-calendar-alt text-primary mr-2" style={{ fontSize: 22 }} />
                          Academic Master Planner
                        </h2>
                        <p className="text-muted m-0 small mt-1">
                          Manage and visualize active curriculum milestones and learning schedules.
                        </p>
                      </div>

                      {/* Tab Navigation Pills */}
                      <div className="d-flex align-items-center">
                        <nav className="d-flex" style={{ background: "#f1f5f9", borderRadius: 12, padding: 4, gap: 4 }}>
                          <button
                            type="button"
                            onClick={() => this.setState({ activeTab: "details" })}
                            style={{
                              padding: "8px 18px",
                              borderRadius: 10,
                              border: "none",
                              fontWeight: 700,
                              fontSize: 13,
                              cursor: "pointer",
                              transition: "all 0.18s ease",
                              background: this.state.activeTab === "details" ? "#ffffff" : "transparent",
                              color: this.state.activeTab === "details" ? "#7c3aed" : "#64748b",
                              boxShadow: this.state.activeTab === "details" ? "0 1px 6px rgba(0,0,0,0.08)" : "none"
                            }}
                          >
                            <i className="fal fa-calendar-alt mr-2" />
                            Planner Details
                          </button>

                          {this.state.userRole === "Admin" && (
                            <button
                              type="button"
                              onClick={() => this.setState({ activeTab: "configuration" })}
                              style={{
                                padding: "8px 18px",
                                borderRadius: 10,
                                border: "none",
                                fontWeight: 700,
                                fontSize: 13,
                                cursor: "pointer",
                                transition: "all 0.18s ease",
                                background: this.state.activeTab === "configuration" ? "#ffffff" : "transparent",
                                color: this.state.activeTab === "configuration" ? "#7c3aed" : "#64748b",
                                boxShadow: this.state.activeTab === "configuration" ? "0 1px 6px rgba(0,0,0,0.08)" : "none"
                              }}
                            >
                              <i className="fal fa-cog mr-2" />
                              Configuration
                            </button>
                          )}
                        </nav>
                      </div>
                    </div>

                    {/* ── Prominent Active Master Planner Header Bar (Planner Details Tab Only) ── */}
                    {this.state.activeTab === "details" && (
                      <div className="active-planner-selector-bar p-3 mb-4 rounded-lg d-flex align-items-center justify-content-between flex-wrap gap-3"
                        style={{ background: "linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%)", border: "1.5px solid #cbd5e1", borderRadius: 14 }}>
                        
                        <div className="d-flex align-items-center flex-wrap gap-3 flex-grow-1">
                          <div style={{
                            width: 44, height: 44, borderRadius: 12,
                            background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#fff", boxShadow: "0 4px 12px rgba(124, 58, 237, 0.25)"
                          }}>
                            <i className="fal fa-book-open" style={{ fontSize: 20 }} />
                          </div>
                          <div>
                            <span className="small text-muted font-weight-bold text-uppercase d-block" style={{ fontSize: "11px", letterSpacing: "0.6px" }}>
                              Active Master Planner Selector
                            </span>
                            {this.state.plannersList && this.state.plannersList.length > 0 ? (
                              <select
                                className="form-control form-control-sm custom-select font-weight-bold text-dark mt-1"
                                style={{
                                  minWidth: 320,
                                  maxWidth: 520,
                                  fontSize: "14px",
                                  height: 38,
                                  borderColor: "#7c3aed",
                                  borderRadius: 10,
                                  background: "#ffffff",
                                  boxShadow: "0 2px 8px rgba(124, 58, 237, 0.08)"
                                }}
                                value={this.state.selectedPlannerId || ""}
                                onChange={(e) => this.handlePlannerChange(e.target.value)}
                              >
                                {this.state.plannersList.map(planner => (
                                  <option key={planner.id} value={String(planner.id)}>
                                    {planner.academic_planner} {planner.stream_name ? `(${planner.stream_name})` : ""}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="small text-muted font-weight-bold text-warning d-block mt-1">
                                ⚠️ No academic planner found in database. Create one below to begin.
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <span className="badge badge-light border text-muted px-3 py-2 font-weight-bold" style={{ borderRadius: 10, fontSize: "12px" }}>
                            <i className="fal fa-info-circle text-primary mr-1" />
                            {this.state.plannersList ? `${this.state.plannersList.length} Active Master Planner(s)` : "Loaded"}
                          </span>
                        </div>
                      </div>
                    )}

                      {/* ── TAB: Planner Details ──────────────────────────── */}
                      {this.state.activeTab === "details" && (
                        <div>
                          {/* Filter and Control Toolbar */}
                          <FilterControlBar
                            searchText={searchText}
                            onSearchChange={this.handleSearchChange}
                            allSubjects={this.getStreamSubjects(activeStream)}
                            activeSubjects={activeSubjects}
                            onSubjectToggle={this.handleSubjectToggle}
                            activeCategory={activeCategory}
                            onCategoryChange={this.handleCategoryChange}
                            viewMode={viewMode}
                            onViewModeChange={this.handleViewModeChange}
                            streamName={activeStream}
                            scopeMode={this.state.scopeMode}
                            onScopeModeChange={this.handleScopeModeChange}
                            selectedBranch={this.state.selectedBranch}
                            onBranchChange={this.handleBranchChange}
                            selectedSection={this.state.selectedSection}
                            onSectionChange={this.handleSectionChange}
                            plannersList={this.state.plannersList}
                            selectedPlannerId={this.state.selectedPlannerId}
                            onPlannerChange={(plannerId) => this.setState({ selectedPlannerId: plannerId }, () => this.fetchPlannerData())}
                            branchesList={this.state.branchesList}
                            sectionsList={this.state.sectionsList}
                            userRole={this.state.userRole}
                            onRoleChange={this.handleRoleChange}
                            onOpenShiftModal={this.handleOpenShiftModal}
                            onOpenCreatePlanner={() => this.setState({ activeTab: "configuration" })}
                            dateFrom={this.state.dateFrom}
                            dateTo={this.state.dateTo}
                            onDateFromChange={this.handleDateFromChange}
                            onDateToChange={this.handleDateToChange}
                            onDateReset={this.handleDateReset}
                            filteredDays={filteredDays}
                            plannerName={activePlannerName}
                          />

                          {/* Scroll Viewport */}
                          <div className="ds-viewport-scroll-area">
                            {/* Empty State — guides user to Configuration tab */}
                            {(!filteredDays || filteredDays.length === 0) && !this.state.loading && (
                              <div className="card text-center p-5 border-0 shadow-sm rounded-lg my-4 bg-white">
                                <div className="card-body">
                                  <i className="fal fa-calendar-plus text-primary mb-3" style={{ fontSize: "48px" }} />
                                  <h5 className="font-weight-bold text-dark mb-2">No Active Academic Planner Found</h5>
                                  <p className="text-muted small mx-auto mb-4" style={{ maxWidth: "480px" }}>
                                    Initialize a new master planner for <strong>{activeStream}</strong> to auto-generate daily syllabus schedules, exam weeks, and section progress tracking.
                                  </p>
                                  <button
                                    type="button"
                                    className="btn btn-primary font-weight-bold px-4 py-2 shadow-sm"
                                    style={{ borderRadius: "24px" }}
                                    onClick={() => this.setState({ activeTab: "configuration" })}
                                  >
                                    <i className="fal fa-cog mr-2" /> Go to Configuration &rarr;
                                  </button>
                                </div>
                              </div>
                            )}

                            {filteredDays && filteredDays.length > 0 && (
                              viewMode === "timeline" ? (
                                <TimelineViewport
                                  filteredDays={filteredDays}
                                  activeSubjects={activeSubjects}
                                  onDayClick={this.handleDayClick}
                                  selectedDay={selectedDay}
                                  searchText={searchText}
                                  streamName={activeStream}
                                  onOpenBulkUpload={() => this.setState({ activeTab: "configuration" })}
                                />
                              ) : viewMode === "table" ? (
                                <TableViewport
                                  filteredDays={filteredDays}
                                  activeSubjects={activeSubjects}
                                  onDayClick={this.handleDayClick}
                                  selectedDay={selectedDay}
                                  searchText={searchText}
                                  streamName={activeStream}
                                />
                              ) : (
                                <CalendarGridViewport
                                  filteredDays={filteredDays}
                                  allDays={filteredDays}
                                  activeSubjects={activeSubjects}
                                  onDayClick={this.handleDayClick}
                                  selectedDay={selectedDay}
                                  searchText={searchText}
                                  streamName={activeStream}
                                />
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {/* ── TAB: Configuration (Admin Only) ──────────────── */}
                      {this.state.activeTab === "configuration" && this.state.userRole === "Admin" && (
                        <ConfigurationPanel
                          plannersList={this.state.plannersList}
                          selectedPlannerId={this.state.selectedPlannerId}
                          branchesList={this.state.branchesList}
                          sectionsList={this.state.sectionsList}
                          academicYearsList={this.state.academicYearsList}
                          institutionId={this.state.institutionId}
                          apolloClient={this.props.client}
                          onCreatePlanner={this.handleCreatePlanner}
                          onBulkUpload={this.handleBulkUpload}
                          onPlannerSelect={(plannerId) => {
                            this.setState({ selectedPlannerId: String(plannerId), activeTab: "details" }, () => {
                              this.fetchPlannerData();
                            });
                          }}
                        />
                      )}

                    </div>{/* end p-4 */}
                  </div>{/* end white card */}

                {/* Floating Go to Top button */}
                {showGoToTop && (
                  <button
                    onClick={this.scrollToTop}
                    className="floating-back-to-top"
                    type="button"
                    title="Go to Top"
                  >
                    <i className="fal fa-chevron-up" />
                  </button>
                )}

              </Container>
            </div>
          </div>

          <Footer />
        </div>

        {/* Slide-out Detail Drawer */}
        <DetailDrawer
          isOpen={isDrawerOpen}
          day={selectedDay}
          onClose={this.handleDrawerClose}
          streamName={activeStream}
          scopeMode={this.state.scopeMode}
          selectedBranch={this.state.selectedBranch}
          selectedSection={this.state.selectedSection}
          userRole={this.state.userRole}
          onSaveDay={this.handleSaveDay}
          onUpdateStatus={this.handleUpdateStatus}
        />

        {/* Bulk Shift Schedule Modal */}
        <ShiftScheduleModal
          show={this.state.isShiftModalOpen}
          onHide={this.handleCloseShiftModal}
          streamName={activeStream}
          onShiftSchedule={this.handleBulkShiftSchedule}
        />

      </div>
    );
  }
}

export default withRouter(withApollo(AcademicDashboard));
